/**
 * useUser Hook
 * Wrapper around Supabase auth to get the current logged-in user and their profile
 *
 * Handles both configured and unconfigured Supabase states gracefully.
 */

import { useCallback, useEffect, useState } from 'react';
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '~/shared/lib/supabase';
import { queryWithAuthRetry } from '~/core/api/executeWithAuthRetry';
import { useAuthStore } from '~/core/store/useAuthStore';
import type { Profile } from '~/types/database';

// ============================================================================
// TYPES
// ============================================================================

export interface UseUserState {
  /** Supabase auth user */
  user: User | null;
  /** User's profile from the profiles table */
  profile: Profile | null;
  /** Current auth session */
  session: Session | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether Supabase is configured */
  isConfigured: boolean;
}

export interface UseUserActions {
  /** Refresh user and profile data */
  refresh: () => Promise<void>;
  /** Sign out the current user */
  signOut: () => Promise<void>;
}

export type UseUserReturn = UseUserState & UseUserActions;

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * useUser - Main authentication hook
 *
 * Provides current user information, profile data, and auth actions.
 * Automatically subscribes to auth state changes.
 * Handles unconfigured Supabase gracefully (returns null state).
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, profile, isLoading, isAuthenticated, signOut } = useUser();
 *
 *   if (isLoading) return <Spinner />;
 *   if (!isAuthenticated) return <LoginPrompt />;
 *
 *   return (
 *     <div>
 *       <p>Welcome, {profile?.name}</p>
 *       <button onClick={signOut}>Sign Out</button>
 *     </div>
 *   );
 * }
 * ```
 */
/**
 * 진행 중인 profile 조회 (auth_user_id 별).
 *
 * useUser 는 훅 인스턴스마다 자기 state 와 자기 fetch 를 갖는데, `usePermission` 이
 * 이 훅을 쓰고 usePermission 은 9개 컴포넌트(PermissionGate 포함)에서 쓰인다.
 * 그래서 부팅 시 마운트된 인스턴스 수만큼 **똑같은 profiles 행을 조회**했다
 * (실앱 실측: `profiles?select=*` 3회).
 *
 * 동시 호출자가 같은 프라미스를 공유하게 해 요청을 1회로 합친다.
 * TTL 캐시는 두지 않는다 — auth 프로필은 세션 변화에 따라 다시 읽혀야 하므로,
 * "동시성"만 합치고 "시간"은 건드리지 않는다(staleness 리스크 0).
 */
const profileInFlight = new Map<string, Promise<Profile | null>>();

export function useUser(): UseUserReturn {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isConfigured = isSupabaseConfigured();

  /**
   * Fetch profile from the profiles table by auth_user_id
   * Note: profile.id is different from auth.user.id
   */
  const fetchProfile = useCallback(async (authUserId: string): Promise<Profile | null> => {
    if (!supabase) return null;
    if (useAuthStore.getState().authLiveness === 'dead') return null;

    // 이미 같은 사용자의 조회가 진행 중이면 그 프라미스를 공유한다(요청 dedupe).
    const pending = profileInFlight.get(authUserId);
    if (pending) return pending;

    const client = supabase;
    const request = (async (): Promise<Profile | null> => {
      try {
        const { data, error: profileError } = await queryWithAuthRetry<Profile>(() =>
          client
            .from('profiles')
            .select('*')
            .eq('auth_user_id', authUserId)
            .single()
        );

        if (profileError) {
          console.error('[useUser] Error fetching profile:', profileError);
          return null;
        }

        return data;
      } catch (err) {
        console.error('[useUser] Error in fetchProfile:', err);
        return null;
      } finally {
        profileInFlight.delete(authUserId);
      }
    })();

    profileInFlight.set(authUserId, request);
    return request;
  }, []);

  /**
   * Load initial session and user data
   */
  const loadUser = useCallback(async () => {
    // If Supabase is not configured, just set loading to false
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get current session
      const { data: { session: currentSession }, error: sessionError } =
        await supabase.auth.getSession();

      if (sessionError) {
        throw sessionError;
      }

      setSession(currentSession);

      if (currentSession?.user) {
        setUser(currentSession.user);

        // Fetch profile data
        const profileData = await fetchProfile(currentSession.user.id);
        setProfile(profileData);
      } else {
        setUser(null);
        setProfile(null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load user';
      setError(message);
      console.error('[useUser] Error loading user:', err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchProfile]);

  /**
   * Handle auth state changes
   */
  const handleAuthStateChange = useCallback(
    async (event: AuthChangeEvent, newSession: Session | null) => {
      setSession(newSession);

      if (newSession?.user) {
        setUser(newSession.user);

        // TOKEN_REFRESHED에서는 profile refetch 스킵 — 토큰만 갱신된 것
        if (event === 'TOKEN_REFRESHED') return;

        // Fetch updated profile
        const profileData = await fetchProfile(newSession.user.id);
        setProfile(profileData);
      } else {
        setUser(null);
        setProfile(null);
      }

      // Reset loading state after auth change
      setIsLoading(false);
    },
    [fetchProfile]
  );

  /**
   * Refresh user and profile data manually
   */
  const refresh = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  /**
   * Sign out the current user
   */
  const signOut = useCallback(async () => {
    if (!supabase) {
      setUser(null);
      setProfile(null);
      setSession(null);
      return;
    }

    try {
      setError(null);
      const { error: signOutError } = await supabase.auth.signOut();

      if (signOutError) {
        throw signOutError;
      }

      setUser(null);
      setProfile(null);
      setSession(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign out';
      setError(message);
      throw err;
    }
  }, []);

  /**
   * Initialize and subscribe to auth changes
   */
  useEffect(() => {
    // Load initial user data
    loadUser();

    // If Supabase is not configured, don't set up listener
    if (!supabase) {
      return;
    }

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      handleAuthStateChange
    );

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [loadUser, handleAuthStateChange]);

  return {
    user,
    profile,
    session,
    isLoading,
    error,
    isAuthenticated: !!user,
    isConfigured,
    refresh,
    signOut,
  };
}

export default useUser;
